import {
  createSystem,
  defaultConfig,
  defineRecipe,
  defineSlotRecipe,
  IconButton,
} from "@chakra-ui/react";
import { defineTextStyles } from "@chakra-ui/react";

const textStyles = defineTextStyles({
  body: {
    description: "The body text style - used in paragraphs",
    value: {
      fontFamily: "Poppins",
      fontWeight: "500",
      fontSize: "16px",
      lineHeight: "24",
      letterSpacing: "0",
      textDecoration: "None",
      textTransform: "None",
    },
  },
});

const buttonRecipe = defineRecipe({
  base: {
    px: 3,
    py: 2,
    fontWeight: "semibold",
    fontFamily: `'Poppins', sans-serif`,
    borderRadius: "{radii.buttonRadius}",
  },
  variants: {
    visual: {
      primary: {
        color: "{colors.buttonColorPrimary}",
        bg: "{colors.buttonBackgroundPrimary}",
        _hover: {
          bg: "{colors.buttonColorPrimary}",
          color: "{colors.buttonBackgroundPrimary}",
        },
      },
      secondary: {
        color: "{colors.buttonColorSecondary}",
        bg: "{colors.buttonBackgroundSecondary}",
        boxShadow: "sm",
        _hover: {
          bg: "{colors.buttonColorSecondary}",
          color: "{colors.buttonBackgroundSecondary}",
        },
      },
      standard: {
        color: "{colors.buttonColorStandard}",
        bg: "{colors.buttonBackgroundStandard}",
        boxShadow: "sm",
        _hover: {
          color: "{colors.buttonColorSecondary}",
          bg: "{colors.buttonBackgroundStandard}",
        },
      },
      IconButton: {
        justifyContent: "center",
        alignItems: "center",
        borderColor: "{colors.borderPrimary}",
        color: "{colors.highlight}",
        bg: "{colors.backgroundSecondary}",
        _hover: {
          bg: "{colors.backgroundPrimary}",
          borderColor: "{colors.highlight}"
        }
      }
    },
  },
  defaultVariants: { visual: "primary" },
});

const cardSlotRecipe = defineSlotRecipe({
  className: "chakra-card",
  slots: ["root", "header", "body", "footer", "title", "description"],
  base: {
    root: {
      px: "6",
      py: "4",
      fontFamily: `'Poppins', sans-serif`,
      boxShadow: "md",
      borderRadius: "{radii.cardRadius}",
    },
    title: {
      color: "{colors.highlight}",
    },
    description: {
      color: "{colors.text}",
    },
  },
  variants: {
    orient: {
      horizontal: {
        root: {
          backgroundColor: "{colors.backgroundPrimary}",
          borderWidth: "0px",
          borderColor: "{colors.backgroundPrimary}",
          flexDir: "row",
        },
      },
      vertical: {
        root: {
          backgroundColor: "{colors.backgroundPrimary}",
          borderWidth: "0px",
          borderColor: "{colors.backgroundPrimary}",
          flexDir: "column",
        },
      },
    },
  },
  defaultVariants: {
    orient: "horizontal",
  },
});

const containerRecipe = defineRecipe({
  base: {
    width: "100%",
    backgroundColor: "{colors.backgroundPrimary}",
    maxWidth: "100vw",
    padding: 4,
    display: "flex",
    flexDir: "column",
    borderRadius: "{radii.containerRadius}",
  },
});

const boxRecipe = defineRecipe({
  base: {
    width: "100%",
    backgroundColor: "{colors.backgroundPrimary}",
    padding: 4,
    display: "flex",
    flexDir: "column",
    borderRadius: "{radii.containerRadius}",
  },
});

const textRecipe = defineRecipe({
  base: {
    fontFamily: `'Poppins', sans-serif`,
    fontWeight: "normal",
    lineHeight: "2",
  },
  variants: {
    type: {
      normal: {
        color: "{colors.text}",
      },
      highlight: {
        color: "{colors.highlight}",
      },
    }, // Add a comma here
  },
  defaultVariants: { type: "normal" },
});

const headingRecipe = defineRecipe({
  base: {
    lineHeight: "4",
    fontWeight: "bold",
    fontFamily: "'Poppins', sans-serif",
  },
  variants: {
    type: {
      normal: {
        color: "{colors.text}",
      },
      highlight: {
        color: "{colors.highlight}",
      },
    },
  },
  defaultVariants: { type: "normal" },
});

const separatorRecipe = defineRecipe({
  base: {
    colorPalette: "gray",
  },
});

const spinnerRecipe = defineRecipe({
  className: "chakra-spinner",
  base: {
    color: "{colors.highlight}",
    display: "flex",
    flexDirection: "column",
    borderStyle: "solid",
    borderWidth: "3px",
    w: "50px",
    h: "50px",
    alignItems: "center",
    justifyContent: "center",
    my: 2,
    mx: 2
  }
  ,
  variants: {
    size: {
      small: {
        w: "25px",
        h: "25px",
      },
      medium: {
        w: "50px",
        h: "50px",
      },
      large: {
        w: "100px",
        h: "100px",
      }
    },
  },
  defaultVariants: { size: "medium" },
})

const dialogSlotRecipe = defineSlotRecipe({
  className: "chakra-dialog",
  slots: [
    "header",
    "body",
    "footer",
    "backdrop",
    "content",
    "positioner",
    "title",
    "description",
  ],
  base: {
    backdrop: {
      bg: "{colors.backdropPrimary}",
      pos: "fixed",
      left: 0,
      top: 0,
      w: "100vw",
      h: "100dvh",
      zIndex: 1000,
    },
    positioner: {
      display: "flex",
      width: "100vw",
      height: "100dvh",
      position: "fixed",
      left: 0,
      top: 0,
      justifyContent: "center",
      alignItems: "center",
    },
    content: {
      fontFamily: `'Poppins', sans-serif`,
      display: "flex",
      flexDirection: "column",
      position: "fixed",
      maxWidth: "80vh",
      width: "100%",
      borderRadius: "lg",
      zIndex: 1010,
      transform: "translate(-50%, -50%)",
      top: "50%",
      left: "50%",
      justifyContent: "center",
      alignItems: "center",
      bg: "{colors.backgroundSecondary}",
      boxShadow: "lg",
      overflow: "auto",
      maxHeight: "80vh",
    },
    header: {
      bg: "{colors.backgroundSecondary}",
      color: "{colors.text}",
    },
    body: {
      bg: "{colors.backgroundSecondary}",
      color: "{colors.text}",
      fontSize: "md",
    },
    footer: {
      bg: "{colors.backgroundSecondary}",
      color: "{colors.text}",
    },
  },
});

const inputRecipe = defineRecipe({
  base: {
    border: '0',
    borderRadius: '{radii.buttonRadius}',
    outline: '1px solid {colors.inputBorder}',
    shadow: 'sm',
    _focusVisible: {
      outlineColor: '{colors.highlight}',
      outline: '1px solid {colors.highlight}',
    },
  },
  variants: {
    visual: {
      filled: {
        backgroundColor: '{colors.inputBox}',
      },
      secondary: {
        backgroundColor: "{colors.backgroundBlack}",
        shadow: "none",
        my: "10px"
      },
      standard: {
        border: "1px solid {colors.text} !important",
        borderRadius: "10px",
        background: "none",
        outline: "none",
        backgroundColor: "transparent",
        _focus: {
          border: "1px solid {colors.buttonBackgroundPrimary} !important",
        }
      }
    }
  },
  defaultVariants: {
    visual: 'filled',
  }
});

const selectSlotRecipe = defineSlotRecipe({
  className: "chakra-native-select",
  slots: ['root', 'trigger', 'valueText', 'content', 'itemGroup', 'item', 'indicator'],
  base: {
    root: {
      backgroundColor: '{colors.inputBox}',
      border: 'none',
      borderRadius: '{radii.buttonRadius}',
      outline: '1px solid {colors.inputBorder}',
      shadow: 'sm',
      _focus: {
        outlineColor: '{colors.highlight}',
        outline: '1px solid {colors.highlight}',
      },
    },
    trigger: {
      background: '{colors.inputBox}',
      _focus: {
        outlineColor: '{colors.highlight}',
        outline: '1px solid {colors.highlight}',
      },
    },
    content: {
      backgroundColor: '{colors.inputBox}',
      display: "flex",
      flexDirection: "column",
      width: "full",
      zIndex: "dropdown",
      _open: {
        animationStyle: "slide-fade-in",
        animationDuration: "fast",
      },
      _closed: {
        animationStyle: "slide-fade-out",
        animationDuration: "fastest",
      },
    },
    indicator: {
      color: '{colors.text}'
    },
    content: { backgroundColor: '{colors.backgroundPrimary}' },
    item: { color: '{colors.text}', backgroundColor: 'transparent' },
  }
});

const checkboxSlotRecipe = defineSlotRecipe({
  slots: ['root', 'label', 'control', 'hiddenInput', 'indicator'],
  base: {
    label: {
      color: '#aaaaaa'
    },
    control: {
      borderRadius: '{radii.buttonRadius}',
      backgroundColor: '{colors.backgroundSecondary}',
      border: '1px solid {colors.highlight}'
    }
  }
});

const fieldSlotRecipe = defineSlotRecipe({
  slots: ['root', 'label'],
  base: {
    label: {
      color: '#aaaaaa'
    },
  },
});

const progressSlotRecipe = defineSlotRecipe({
  slots: ['root', 'track', 'range'],
  base: {
    range: { backgroundColor: '{colors.text}' },
    track: { backgroundColor: '{colors.backgroundPrimary}' }
  }
});

const breadcrumbSlotRecipe = defineSlotRecipe({
  slots: ["link", "currentLink", "item", "list", "root", "ellipsis", "separator"],
  base: {},
  variants: {
    visual: {
      standard: {
        link: {
          color: '{colors.faded} !important',
          _hover: {
            color: '{colors.text} !important'
          }
        },
        currentLink: { color: '{colors.text} !important' },
        _hover: {
          link: { color: '{colors.text} !important' }
        }
      }
    }
  },
  defaultVariants: { visual: 'standard' },
});

const tabsSlotRecipe = defineSlotRecipe({
  slots: ['root', 'list', 'trigger', 'content', 'indicator'],
  base: {
    root: {
      backgroundColor: '{colors.backgroundSecondary}',
      width: 'full',
      borderRadius: '{radii.containerRadius}'
    },
    trigger: { backgroundColor: 'transparent' },
    list: { border: '1px solid {colors.borderPrimary} !important' },
  },
  variants: {
    visual: {
      standard: {
        list: { backgroundColor: 'transparent !important' },
      },
      secondary: {
        list: {
          bg: "{colors.backgroundPrimary}",
          p: "1",
        },
        trigger: {
          color: "{colors.faded}",
          "--indicator-color": "{colors.highlight} !important",
          "&[data-selected]": {
            color: "{colors.highlight} !important",
          },
        },
      }
    }
  },
  defaultVariants: { visual: 'standard' }
});

const stepsSlotRecipe = defineSlotRecipe({
  slots: ['root', 'list', 'item', 'trigger', 'indicator', 'status', 'separator'],
  base: {
    separator: { backgroundColor: '{colors.faded}', },
    indicator: { borderColor: '{colors.faded}' },
    title: { color: '{colors.text}' },
  },
  variants: {
    visual: {
      primary: {
        indicator: {
          _complete: {
            backgroundColor: '{colors.backgroundStep}',
            borderColor: '{colors.backgroundStep}',
            color: '{colors.backgroundIndicator}',
          },
        },
        separator: {
          "&[data-complete]": {
            bgColor: "{colors.backgroundIndicator} !important"
          }
        }
      },
    }
  }
});

const switchSlotRecipe = defineSlotRecipe({
  slots: ['root', 'control', 'indicator', 'thumb', 'thumbIndicator', 'label'],
  base: {
    label: { color: '{colors.faded}' },
    control: { 
      backgroundColor: '{colors.backgroundPrimary} !important',
      _checked: {
        backgroundColor: '{colors.text} !important',
      }
    },
  }
});

const fieldsetSlotRecipe = defineSlotRecipe({
  slots: ["root"],
  base: {
    root: {
      display: "flex",
      flexDirection: "column",
      width: "full",
      alignItems: "center",
      justifyContent: "center"
    }
  }
})

const radioCardSlotRecipe = defineSlotRecipe({
  slots: ["root", "item", "itemControl"],
  base: {
    root: {
      display: "flex",
    },
  }
})

const tooltipSlotRecipe = defineSlotRecipe({
  slots: ["content"],
  base: {
    content: {
      bg: "{colors.backgroundPrimary}",
      color: "{colors.backgroundWhite}",
      p: "2"
    }
  }
})

const drawerSlotRecipe = defineSlotRecipe({
  slots: ["backdrop", "content"],
  base: {

    backdrop: {
      bg: "{colors.backdropPrimary}",
      pos: "fixed",
      insetInlineStart: 0,
      top: 0,
      w: "100vw",
      h: "100dvh",
      zIndex: "modal",
    },

    content: {
      display: "flex",
      flexDirection: "column",
      position: "fixed",
      width: "300px",
      height: "100dvh",
      left: 0,
      top: 0,
      outline: 0,
      zIndex: "modal",
      textStyle: "sm",
      maxH: "100dvh",
      bg: "{colors.backgroundPrimary}",
      color: "{colors.text}",
      boxShadow: "lg",
      _open: {
        animationDuration: "slowest",
        animationTimingFunction: "ease-in-smooth",
      },
      _closed: {
        animationDuration: "slower",
        animationTimingFunction: "ease-in-smooth",
      },
    },
    header: {
      flex: 0,
      px: "2",
      py: 4
    },
    body: {
      px: "2",
      pb: "4",
      flex: "1",
      overflow: "auto",
      maxH: { base: "55vh", sm: "60vh" },
    },
  }
})

// const drawerSlotRecipe = defineSlotRecipe({
//   slots: ['root', 'trigger', 'positioner', 'content', 'closeTrigger', 'header', 'title', 'body'],
//   base: {
//     content: {
//       bg: '{colors.backgroundPrimary}',
//     },
//     closeTrigger: {
//       cursor: 'pointer'
//     }
//   }
// })

export const system = createSystem(defaultConfig, {
  globalCss: {
    '*': {
      fontFamily: '{fonts.poppins}',
    }
  },
  theme: {
    tokens: {
      fonts: { poppins: `'Poppins', sans-serif` },
      colors: {
        // Text Colors
        text: "#eee",
        highlight: "#eac100",
        faded: "#888888",
        textPeh: "#fff",
        textChaan: "#edc500",
        textReject: "#e74c3c",



        // Background Colors
        backgroundPrimary: "#111", 
        backgroundSecondary: "#282828",
        backgroundBlack: "#000",
        backgroundWhite: "white",
        backgroundStep: "#58b75c",
        backgroundIndicator: "white",
        backgroundCard: "rgba(0, 0, 0, 0.2)",

        //gradient
        signupGradient: "linear-gradient(160deg, #000, #222, #000)",

        //border colors
        borderPrimary: "#333",

        //backdrop colors
        backdropPrimary: "rgba(0, 0, 0, 0.7)",

        // Button Colors
        buttonColorPrimary: "#222",
        buttonBackgroundPrimary: "#eac500",
        buttonColorSecondary: "#eac500",
        buttonBackgroundSecondary: "#222",
        buttonColorStandard: "#eee",
        buttonBackgroundStandard: "#282828",

        // Form Colors
        inputBox: '#1d1d1d'
      },
      radii: {
        containerRadius: "10px",
        cardRadius: "8px",
        buttonRadius: "6px",
      },
    },
    textStyles: textStyles,
    recipes: {
      button: buttonRecipe,
      text: textRecipe,
      heading: headingRecipe,
      container: containerRecipe,
      separator: separatorRecipe,
      box: boxRecipe,
      heading: headingRecipe,
      spinner: spinnerRecipe,
      input: inputRecipe,
      textarea: inputRecipe,
    },
    slotRecipes: {
      card: cardSlotRecipe,
      dialog: dialogSlotRecipe,
      select: selectSlotRecipe,
      checkbox: checkboxSlotRecipe,
      field: fieldSlotRecipe,
      breadcrumb: breadcrumbSlotRecipe,
      progress: progressSlotRecipe,
      tabs: tabsSlotRecipe,
      steps: stepsSlotRecipe,
      fieldset: fieldsetSlotRecipe,
      radioCard: radioCardSlotRecipe,
      switch: switchSlotRecipe,
      tooltip: tooltipSlotRecipe,
      drawer: drawerSlotRecipe
    },
  },
});